// services/innovationService.js
import { storage } from '../utils/storage';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

class InnovationService {
  constructor() {
    this.baseUrl = `${API_BASE}/api/data/innovation_ideas`;
  }

  async getHeaders() {
    const token = await storage.getItem('auth_token');
    return {
      'X-API-Key': API_KEY,
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Get all ideas with filters
  async getIdeas(filters = {}) {
    try {
      const headers = await this.getHeaders();
      const businessId = await storage.getItem('company_id');
      
      const queryParams = new URLSearchParams({
        company_id: businessId,
        sort_by: 'created_at',
        sort_order: 'DESC',
        ...filters
      }).toString();

      const response = await fetch(`${this.baseUrl}?${queryParams}`, { headers });
      return await response.json();
    } catch (error) {
      console.error('Error fetching ideas:', error);
      return { success: false, error: error.message };
    }
  }

  // Get single idea
  async getIdea(id) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/${id}`, { headers });
      return await response.json();
    } catch (error) {
      console.error('Error fetching idea:', error);
      return { success: false, error: error.message };
    }
  }

  // Create new idea
  async createIdea(ideaData) {
    try {
      const headers = await this.getHeaders();
      const businessId = await storage.getItem('company_id');
      const userId = await storage.getItem('user_id');
      
      // Generate idea code
      const prefix = 'INN';
      const businessCode = businessId.slice(-4);
      const titleInitials = ideaData.idea_title
        .split(' ')
        .map(w => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 3);
      const timestamp = Date.now().toString().slice(-6);
      const ideaCode = `${prefix}-${businessCode}-${titleInitials}-${timestamp}`;

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...ideaData,
          company_id: parseInt(businessId),
          submitted_by: parseInt(userId),
          idea_code: ideaCode,
          review_status: 'pending',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
      });

      return await response.json();
    } catch (error) {
      console.error('Error creating idea:', error);
      return { success: false, error: error.message };
    }
  }

  // Update idea
  async updateIdea(id, updates) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...updates,
          updated_at: new Date().toISOString()
        })
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating idea:', error);
      return { success: false, error: error.message };
    }
  }

  // Review idea (for managers)
  async reviewIdea(id, review) {
    try {
      const headers = await this.getHeaders();
      const userId = await storage.getItem('user_id');
      
      const overallScore = (
        (review.priority_score || 0) + 
        (review.feasibility_score || 0) + 
        (review.impact_score || 0) + 
        (review.alignment_score || 0)
      ) / 4;

      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          review_status: review.decision === 'approve' ? 'approved' : 'rejected',
          reviewed_by: parseInt(userId),
          review_date: new Date().toISOString(),
          review_comments: review.comments,
          priority_score: review.priority_score,
          feasibility_score: review.feasibility_score,
          impact_score: review.impact_score,
          alignment_score: review.alignment_score,
          overall_score: overallScore.toFixed(2),
          decision: review.decision,
          decision_reason: review.reason,
          assigned_to_project: review.assign_to_project || false,
          next_steps: review.next_steps,
          updated_at: new Date().toISOString()
        })
      });
      return await response.json();
    } catch (error) {
      console.error('Error reviewing idea:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete idea
  async deleteIdea(id) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers
      });
      return await response.json();
    } catch (error) {
      console.error('Error deleting idea:', error);
      return { success: false, error: error.message };
    }
  }

  // Get innovation statistics
  async getStats() {
    try {
      const result = await this.getIdeas({ limit: 1000 });
      if (!result.success) return result;

      const ideas = result.data;
      const stats = {
        total: ideas.length,
        byStatus: {
          approved: ideas.filter(i => i.review_status === 'approved').length,
          pending: ideas.filter(i => i.review_status === 'pending').length,
          under_review: ideas.filter(i => i.review_status === 'under_review').length,
          rejected: ideas.filter(i => i.review_status === 'rejected').length
        },
        byType: {},
        averageScores: {
          priority: 0,
          feasibility: 0,
          impact: 0,
          alignment: 0,
          overall: 0
        },
        totalEstimatedCost: 0,
        highImpact: 0
      };

      let scoreCount = 0;
      ideas.forEach(idea => {
        // Count by type
        if (idea.innovation_type) {
          stats.byType[idea.innovation_type] = (stats.byType[idea.innovation_type] || 0) + 1;
        }

        // Sum scores
        if (idea.priority_score) {
          stats.averageScores.priority += parseFloat(idea.priority_score);
          stats.averageScores.feasibility += parseFloat(idea.feasibility_score || 0);
          stats.averageScores.impact += parseFloat(idea.impact_score || 0);
          stats.averageScores.alignment += parseFloat(idea.alignment_score || 0);
          stats.averageScores.overall += parseFloat(idea.overall_score || 0);
          scoreCount++;
        }

        // Sum costs
        if (idea.estimated_cost) {
          stats.totalEstimatedCost += parseFloat(idea.estimated_cost);
        }

        // Count high impact
        if (idea.potential_impact === 'HIGH') {
          stats.highImpact++;
        }
      });

      // Calculate averages
      if (scoreCount > 0) {
        Object.keys(stats.averageScores).forEach(key => {
          stats.averageScores[key] = (stats.averageScores[key] / scoreCount).toFixed(2);
        });
      }

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error calculating stats:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new InnovationService();